import { useMemo } from "react";
import { BreadTransaction } from "breadcommon";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { STANDARD_MAIN_CONTENT_CARD_PADDING_X } from "../rye/RyeMainContentContainerBase";
import {
  RyeMainContentScrollShadow,
  RyeScrollListener,
  useRyeScrolled,
} from "../rye/RyeMainContentScrollShadow";
import { TransactionsTable } from "../transactions_screen/TransactionsTable";
import { TransactionsColumnHeaders } from "../transactions_screen/TransactionsColumnHeaders";
import {
  TransactionsAmountWidthContext,
  useTransactionsAmountWidth,
} from "../transactions_screen/transactionsAmountWidth";
import { buildClasses } from "../utils/buildClasses";

/** The transactions behind a chart selection; the chart owns the selection and its backing data. */
export function TransactionsDrilldownPane({
  groupLabel,
  transactions,
  explanation,
  onClose,
}: {
  groupLabel: string;
  transactions: BreadTransaction[];
  explanation?: string;
  onClose: () => void;
}): JSX.Element {
  const sortedTransactions = useMemo(
    () => [...transactions].sort((a, b) => b.date.valueOf() - a.date.valueOf()),
    [transactions],
  );
  const explanationOnly = transactions.length === 0 && Boolean(explanation);
  // Like the Transactions screen, the column headers sit above the scrolling
  // table, with the explanation above them, so the table scrolls on its own.
  const { scrolled, onScroll: onTableScroll } = useRyeScrolled();
  const amountWidth = useTransactionsAmountWidth(transactions);
  const explanationText = explanation && (
    <p className={buildClasses("text-sm", "text-on-surface-600", "py-4")}>
      {explanation}
    </p>
  );

  return (
    <TransactionsAmountWidthContext.Provider value={amountWidth}>
      <RyeMainContentCard
        padTop={false}
        roundedTl={false}
        roundedTr={false}
        roundedBl={false}
        header={{
          title: groupLabel,
          subtitle: explanationOnly
            ? undefined
            : `${transactions.length.toLocaleString()} ${transactions.length === 1 ? "transaction" : "transactions"}`,
          onClose,
          closeLabel: "Close transactions",
        }}
        belowHeader={
          !explanationOnly && (
            <>
              {explanationText && (
                <div className={STANDARD_MAIN_CONTENT_CARD_PADDING_X}>
                  {explanationText}
                </div>
              )}
              <TransactionsColumnHeaders disableFilteringAndSorting={true} />
              <RyeMainContentScrollShadow visible={scrolled} />
            </>
          )
        }
      >
        {explanationOnly ? (
          explanationText
        ) : (
          <>
            <RyeScrollListener onScroll={onTableScroll} />
            <TransactionsTable
              sortedFilteredTransactions={sortedTransactions}
              disableFilteringAndSorting={true}
              bottomBufferHeight="sm"
            />
          </>
        )}
      </RyeMainContentCard>
    </TransactionsAmountWidthContext.Provider>
  );
}
